import type { FC } from "react";

import { colorsPalettes } from "@similarweb/styles";
import { ChangeTooltip } from "@similarweb/ui-components";

import type { GranularityNames } from "app/UtilitiesAndConstants/Constants/Moment";
import { translate } from "app/filters/i18nFilter";
import { percentageSignFilter } from "app/filters/ngFilters";
import ChartTooltipAlert from "app/pages/app-intelligence/app-analysis/pages/Engagement/components/ChartTooltipAlert/ChartTooltipAlert";
import { getTooltipHeader } from "app/UtilitiesAndConstants/UtilitiesComponents/chartTooltipHeaderWithMTDSupport";
import type { Point } from "highcharts";

const MonthlyGranularity = "Monthly";

export const getGA4AlertText = (GA4FirstDataPointDate, xPoint) => {
    let domainsToText = "";
    if (!GA4FirstDataPointDate) {
        return;
    }
    GA4FirstDataPointDate.forEach((domain) => {
        if (domain.date === xPoint) {
            const domainPrefix = domainsToText === "" ? " " : " & ";
            domainsToText = domainsToText.concat(domainPrefix, domain.domain);
        }
    });
    return domainsToText;
};

export const getChange = (previousDataPoint, change) => {
    return previousDataPoint
        ? change !== 0 && percentageSignFilter()(change, 2)
        : translate("common.tooltip.change.new");
};

export type GetTooltipHeader = (
    timeGranularity: GranularityNames,
    points: readonly any[],
    lastSupportedDate?: string,
    asText?: boolean,
    alertMessage?: string,
) => string;

export const CompareModeTooltip: FC<{
    points: Array<{ y?; series?; point? }>;
    yFormatter;
    getChangeColor?;
    timeGranularity: GranularityNames;
    lastSupportedDate?: string;
    meta?: { isSingleMode?; isNonTotalWebSource? };
    totalAggregate?: (desktop: number, mobile: number) => number;
    GA4FirstDataPointDate?: any;
    tableHeaders: string[];
    showTotal?: boolean;
    getTooltipHeader?: GetTooltipHeader;
    x: string;
}> = (props) => {
    const {
        points,
        yFormatter,
        getChangeColor,
        timeGranularity,
        lastSupportedDate,
        meta = {},
        totalAggregate,
        GA4FirstDataPointDate,
        tableHeaders,
        showTotal = true,
    } = props;
    const { isSingleMode, isNonTotalWebSource } = meta;

    const changeTooltipProp = ({ y, series, point }) => {
        const pointIndex = point.index;
        const previousDataPoint: Point["y"] = pointIndex === 0 ? series.data[pointIndex].y : series.data[pointIndex - 1].y;
        const change = y / previousDataPoint - 1;
        return {
            value: yFormatter({ value: y }),
            color: series.color,
            displayName: series.name,
            change: previousDataPoint !== 0 && getChange(previousDataPoint, change),
        };
    };
    const changeTooltipProps = {
        header: props?.getTooltipHeader
            ? props.getTooltipHeader(timeGranularity, points, lastSupportedDate)
            : getTooltipHeader(timeGranularity, points, lastSupportedDate),
        getChangeColor,
        tableHeaders,
        tableRows: points.map(changeTooltipProp),
        showChangeColumn: timeGranularity === MonthlyGranularity,
    };
    const ga4AlertText = getGA4AlertText(GA4FirstDataPointDate, props.x);
    // adding the "total" row
    if (isSingleMode && !isNonTotalWebSource && points[0] && points[1] && totalAggregate && showTotal) {
        const desktopPoint = points[0];
        const mobilePoint = points[1];
        const isFirstIndex = desktopPoint.point.index === 0 && mobilePoint.point.index === 0;
        const previousTotalValue =
            !isFirstIndex &&
            totalAggregate(
                desktopPoint.series.data[desktopPoint.point.index - 1].y,
                mobilePoint.series.data[mobilePoint.point.index - 1].y,
            );
        const currentTotalValue = totalAggregate(desktopPoint.y, mobilePoint.y);
        const totalChange = !isFirstIndex && currentTotalValue / previousTotalValue - 1;

        const total = {
            color: colorsPalettes.sky[300],
            value: yFormatter({ value: currentTotalValue }),
            displayName: "Total",
            change: isFinite(totalChange) && totalChange && getChange(previousTotalValue, totalChange),
            type: "total",
        };
        const totalProps = {
            tableRows: [...changeTooltipProps.tableRows, total],
        };
        const changeTooltipPropsWithTotal = { ...changeTooltipProps, ...totalProps };
        return (
            <ChangeTooltip {...changeTooltipPropsWithTotal}>
                {ga4AlertText ? (
                    <ChartTooltipAlert
                        text={translate("website.analysis.traffic.and.engagement.overtime.tooltip.ga4.alert", {
                            domains: ga4AlertText,
                        })}
                    />
                ) : null}
            </ChangeTooltip>
        );
    }
    
    return (
        <ChangeTooltip {...changeTooltipProps}>
            {ga4AlertText ? (
                <ChartTooltipAlert
                    text={translate("website.analysis.traffic.and.engagement.overtime.tooltip.ga4.alert", {
                        domains: ga4AlertText,
                    })}
                />
            ) : null}
        </ChangeTooltip>
    );
};
